import dayjs, { Dayjs } from "dayjs";
import { UnassignedPicking } from "app/types/route";
import { getRelationName } from "app/utils/odooDisplay";
import { resolveUserTimezone } from "app/utils/timezone";

export type UnassignedOrdersSortKey =
  | "name"
  | "customer"
  | "scheduled_date"
  | "origin"
  | "loads";

export type UnassignedOrdersSortDirection = "asc" | "desc";

export type UnassignedOrdersFilters = {
  search: string;
  scheduledFrom: Dayjs | null;
  scheduledTo: Dayjs | null;
  name: string;
  customer: string;
  origin: string;
  loads: string;
};

export const EMPTY_UNASSIGNED_ORDERS_FILTERS: UnassignedOrdersFilters = {
  search: "",
  scheduledFrom: null,
  scheduledTo: null,
  name: "",
  customer: "",
  origin: "",
  loads: "",
};

function getScheduledDayKey(
  value: string | undefined,
  timezone: string
): string | null {
  if (!value) {
    return null;
  }

  const parsed = dayjs.utc(value);
  if (!parsed.isValid()) {
    return null;
  }

  try {
    return parsed.tz(resolveUserTimezone(timezone)).format("YYYY-MM-DD");
  } catch {
    return parsed.format("YYYY-MM-DD");
  }
}

function matchesSearch(order: UnassignedPicking, query: string): boolean {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  const haystack = [
    order.name,
    getRelationName(order.partner_id),
    order.origin,
    getRelationName(order.driver_id),
    order.state,
    String(order.shipment_packages_count ?? ""),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalized);
}

function matchesTextColumn(value: string, filter: string): boolean {
  const normalized = filter.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return value.toLowerCase().includes(normalized);
}

function matchesScheduledRange(
  order: UnassignedPicking,
  scheduledFrom: Dayjs | null,
  scheduledTo: Dayjs | null,
  timezone: string
): boolean {
  const dayKey = getScheduledDayKey(order.scheduled_date, timezone);
  if (!dayKey) {
    return !scheduledFrom && !scheduledTo;
  }

  if (scheduledFrom && dayKey < scheduledFrom.format("YYYY-MM-DD")) {
    return false;
  }
  if (scheduledTo && dayKey > scheduledTo.format("YYYY-MM-DD")) {
    return false;
  }
  return true;
}

export function filterUnassignedOrders(
  orders: UnassignedPicking[],
  filters: UnassignedOrdersFilters,
  timezone: string
): UnassignedPicking[] {
  return orders.filter(
    (order) =>
      matchesSearch(order, filters.search) &&
      matchesTextColumn(order.name || "", filters.name) &&
      matchesTextColumn(getRelationName(order.partner_id), filters.customer) &&
      matchesTextColumn(order.origin || "", filters.origin) &&
      matchesTextColumn(String(order.shipment_packages_count ?? ""), filters.loads) &&
      matchesScheduledRange(
        order,
        filters.scheduledFrom,
        filters.scheduledTo,
        timezone
      )
  );
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
}

export function sortUnassignedOrders(
  orders: UnassignedPicking[],
  sortKey: UnassignedOrdersSortKey,
  direction: UnassignedOrdersSortDirection
): UnassignedPicking[] {
  const multiplier = direction === "asc" ? 1 : -1;

  return [...orders].sort((left, right) => {
    let result = 0;

    switch (sortKey) {
      case "name":
        result = compareStrings(left.name || "", right.name || "");
        break;
      case "customer":
        result = compareStrings(
          getRelationName(left.partner_id),
          getRelationName(right.partner_id)
        );
        break;
      case "scheduled_date": {
        const leftTime = dayjs.utc(left.scheduled_date || "").valueOf();
        const rightTime = dayjs.utc(right.scheduled_date || "").valueOf();
        const leftValid = Number.isFinite(leftTime);
        const rightValid = Number.isFinite(rightTime);
        if (!leftValid && !rightValid) {
          result = 0;
        } else if (!leftValid) {
          result = 1;
        } else if (!rightValid) {
          result = -1;
        } else {
          result = leftTime - rightTime;
        }
        break;
      }
      case "origin":
        result = compareStrings(left.origin || "", right.origin || "");
        break;
      case "loads":
        result =
          (left.shipment_packages_count ?? 0) -
          (right.shipment_packages_count ?? 0);
        break;
      default:
        result = 0;
    }

    return result * multiplier;
  });
}

export function hasActiveUnassignedOrdersFilters(
  filters: UnassignedOrdersFilters
): boolean {
  return Boolean(
    filters.search.trim() ||
      filters.scheduledFrom ||
      filters.scheduledTo ||
      filters.name.trim() ||
      filters.customer.trim() ||
      filters.origin.trim() ||
      filters.loads.trim()
  );
}

export function hasActiveUnassignedOrdersColumnFilters(
  filters: UnassignedOrdersFilters
): boolean {
  return Boolean(
    filters.scheduledFrom ||
      filters.scheduledTo ||
      filters.name.trim() ||
      filters.customer.trim() ||
      filters.origin.trim() ||
      filters.loads.trim()
  );
}
